<template>
  <div
    v-if="questionnaire"
    class="pb-1"
  >
    <b-row>
      <b-col cols="8">
        <div class="position-relative">
          <img
            v-if="questionnaire.cover"
            :src="questionnaire.cover"
            alt="Image"
            class="card-img-top"
          >
          <div
            v-else
            class="card-img-top"
            style="min-height: 200px; background-color: #b1b1b1"
          />
          <div
            class="position-absolute w-100 d-flex justify-content-end align-items-center"
            style="top:10px; right: 10px; width: 100%;"
          >
            <b-badge
              v-for="(label, labelIndex) in getTargetLabels(questionnaire)"
              :key="`label-${labelIndex}`"
              pill
              :class="`badge-glow font-weight-normal card-status text-capitalize ${labelIndex ? 'ml-50' : ''}`"
              variant="light-primary"
              :style="{ color: `${questionnaireTargetLabelsColor(labelIndex)} !important` }"
            >
              {{ label }}
            </b-badge>
          </div>
        </div>
        <b-card class="mb-2">
          <span class="card-title text-capitalize">
            {{ questionnaire.title }}
          </span>
          <div class="card-sub-title mt-1 d-flex align-items-center">
            <div class="w-50">
              <b-avatar
                variant="light-primary"
                :text="getInitials(questionnaire.publisher)"
              />
              {{ $t("questionnaire.by") }}
              <span class="font-weight-bolder text-capitalize">
                {{ questionnaire.publisher }}
              </span>
            </div>
            <div
              class="w-50"
              style="display: flex; justify-content: flex-end; align-items: center; border-left: 1px solid #EBE9F1"
            >
              <span
                v-if="!questionnaire.isDraft"
              >
                {{ getExpiredDate(questionnaire) }}
              </span>
              <b-badge
                pill
                :variant="questionnaireStatusVariant(questionnaire)"
                class="ml-50 badge-glow font-weight-normal card-status text-capitalize"
              >
                <feather-icon
                  icon="ClockIcon"
                  size="16"
                />
                {{ questionnaireStatus(questionnaire) }}
              </b-badge>
            </div>
          </div>

          <b-card-text class="mt-1">
            {{ questionnaire.summary }}
          </b-card-text>

          <template #footer>
            <div class="d-flex justify-content-between">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="16"
                />
                <span>
                  : {{ questionnaire.answersCount }} {{ $t("questionnaire.answers") }}
                </span>
                <feather-icon
                  icon="EyeIcon"
                  size="16"
                  class="ml-1"
                />
                <span>
                  : {{ questionnaire.viewDetail ? questionnaire.viewDetail.count : 0 }} {{ $t("questionnaire.views") }}
                </span>
                <feather-icon
                  icon="ArchiveIcon"
                  size="16"
                  class="ml-1"
                />
                <span>
                  : {{ questionnaire.targetSize }} {{ $t('questionnaire.receives') }}
                </span>
              </div>
            </div>
          </template>
        </b-card>
      </b-col>
      <b-col
        cols="4"
        class="d-flex flex-column"
      >
        <b-card
          no-body
          class="mb-2"
          style="flex: 1"
        >
          <b-card-body>
            <vue-apex-charts
              id="answersStatistic"
              type="radialBar"
              height="400"
              :options="chartOptions"
              :series="series"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <template
      v-if="questionnaire.document && questionnaire.document.length > 0"
    >
      <div
        v-for="document in questionnaire.document"
        :key="document.uuid"
      >
        <single-choice-question
          v-if="document.type == TYPES.SINGLE"
          :document="document"
          @show-vote-list="showVoteList"
        />

        <multi-choice-question
          v-if="document.type == TYPES.MULTIPLE"
          :document="document"
          @show-vote-list="showVoteList"
        />

        <text-question
          v-if="
            document.type == TYPES.TEXT_SHORT ||
              document.type == TYPES.TEXT_LONG
          "
          :document="document"
          :target-size="questionnaire.targetSize"
          @show-user-answers="showTextQuestionAnswer"
        />
        <date-question
          v-if="
            document.type == TYPES.DATE ||
              document.type == TYPES.RANGE
          "
          :document="document"
          @show-vote-list="showVoteList"
        />
      </div>
    </template>

    <vote-list
      :users="userVotes"
      :question-type="questionType"
      :list-title="listTitle"
    />

    <text-question-answer :contents="content" />
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BRow,
  BCol,
  BAvatar,
  BBadge,
  BCardBody,
  VBModal,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import {
  getInitials, handleTimezoneDiff, questionnaireStatus, questionnaireStatusVariant,
  getTargetLabels, questionnaireTargetLabelsColor,
} from '@hw-core/utils'
import { COLORS } from '@/shared/constants'
import { TYPES } from '@/shared/constants/questionnaire'
import SingleChoiceQuestion from './SingleChoiceQuestion.vue'
import MultiChoiceQuestion from './MultiChoiceQuestion.vue'
import TextQuestion from './TextQuestion.vue'
import DateQuestion from './DateQuestion.vue'
import VoteList from './VoteList.vue'
import TextQuestionAnswer from './TextQuestionAnswer.vue'
// eslint-disable-next-line import/no-cycle
import useQuestionnaireView from './useQuestionnaireView'

export default {
  directives: {
    'b-modal': VBModal,
  },
  components: {
    BCard,
    BCardText,
    BRow,
    BCol,
    BAvatar,
    BBadge,
    VueApexCharts,
    BCardBody,
    SingleChoiceQuestion,
    MultiChoiceQuestion,
    TextQuestion,
    DateQuestion,
    VoteList,
    TextQuestionAnswer,
  },
  data() {
    return {
      TYPES,
      userVotes: [],
      questionType: '',
      content: [],
      listTitle: '',
    }
  },
  computed: {
    views() {
      if (this.questionnaire.viewDetail && this.questionnaire.viewDetail.count && this.questionnaire.targetSize) {
        return ((this.questionnaire.viewDetail.count * 100) / this.questionnaire.targetSize).toFixed(2)
      }
      return 0
    },
    answers() {
      if (this.questionnaire.answersCount && this.questionnaire.targetSize) {
        return ((this.questionnaire.answersCount * 100) / this.questionnaire.targetSize).toFixed(2)
      }
      return 0
    },
    series() {
      return [100, this.views, this.answers]
    },
    chartOptions() {
      return {
        colors: [COLORS.YELLOW, COLORS.TEAL, COLORS.INDIGO],
        plotOptions: {
          radialBar: {
            size: 185,
            hollow: {
              size: '25%',
            },
            track: {
              margin: 15,
            },
            dataLabels: {
              name: {
                fontSize: '2rem',
                fontFamily: 'Montserrat',
              },
              value: {
                fontSize: '1rem',
                fontFamily: 'Montserrat',
              },
              total: {
                show: true,
                fontSize: '1rem',
                label: this.$t('questionnaire.receives'),
                formatter: () => this.questionnaire.targetSize ?? 0,
              },
            },
          },
        },
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'center',
        },
        stroke: {
          lineCap: 'round',
        },
        labels: [
          this.$t('questionnaire.receives'),
          this.$t('questionnaire.views'),
          this.$t('questionnaire.answers'),
        ],
      }
    },
  },
  methods: {
    getTargetLabels,
    questionnaireTargetLabelsColor,
    getInitials,
    questionnaireStatus,
    questionnaireStatusVariant,
    getExpiredDate(item) {
      return handleTimezoneDiff(item.expirationDate.toDate())
    },
    showVoteList(users, questionType = '', title) {
      this.userVotes = users
      this.questionType = questionType
      this.listTitle = title
      this.$bvModal.show('vote-list-modal')
    },
    showTextQuestionAnswer(content) {
      this.content = content
      this.$bvModal.show('people-text-question-list-modal')
    },
  },
  setup() {
    const {
      questionnaire,
      fetchData,
    } = useQuestionnaireView()

    fetchData()

    return {
      questionnaire,
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep div {
  .apexcharts-canvas {
    .apexcharts-legend {
      justify-content: space-around;
    }
  }
}
</style>
